// Feature: M004-F0713. Tests: T-M004-0205.
//
// WebUI-authored words for the transaction history (design M004-F0712-F0716 sections 2.4, 2.4b, 2.6).
// ACCT-TX-011: a sentence Core (or the Ledger) sends is shown VERBATIM and never composed here. These tables hold
// ONLY what the enums do not carry: the stage pill label, the short tag beside a timeline row, the calendar condition
// badge, and the FALLBACK sentence used when the wire sent none (always shown with the muted "(no description
// supplied)" suffix so a Core gap is visible).
//
// Every `TransactionStage` and every journal `action` of the pinned Spec enum (1.12.0) has a row here:
// T-M004-0205 iterates the pinned openapi.json enums, so a Spec addition cannot ship without copy.
import { formatMinorAmount } from '../bookings-admin/billingPipelineModel';

export const NO_DESCRIPTION_SUFFIX = '(no description supplied)';

// stage -> { label, fallback(ctx) }. `ctx` = { amount, customer, invoiceNumber, entry, reason, who, date }.
const S = (label, fallback) => ({ label, fallback });
export const STAGE_COPY = Object.freeze({
  captured: S('Booked', () => "Core has recorded this booking's charge. It has no price yet."),
  priced: S('Priced', (c) => `Core has a price of ${c.amount || 'not yet known'}. It can be billed once the session has ended.`),
  settled: S('Session ended', () => 'The session has ended.'),
  awaiting_approval: S('Awaiting approval', () => 'This cost is waiting for you to approve it in Billing.'),
  returned_for_reprice: S('Returned for repricing', () => 'This cost was sent back because its price changed. It needs approving again.'),
  sending_to_ledger: S('Sending to the Ledger', () => 'Core has approved this cost and is sending it to the Ledger. The Ledger has not confirmed it yet.'),
  handed_off: S('With the Ledger', () => "The Ledger holds this cost."),
  advice: S('In Advice', () => 'This cost is in Advice, not on an invoice. It can be moved to the next invoice.'),
  draft: S('On the next invoice', (c) => `This cost is a line on ${c.customer || "the customer's"} next invoice. It can still be changed or moved back to Advice until it is written to the ledger.`),
  issued: S('Invoiced', (c) => (c.invoiceNumber ? `This cost is on invoice ${c.invoiceNumber}, which has been issued.` : 'This cost is on an invoice that has been issued.')),
  posted: S('Posted to the ledger', (c) => `${c.entry ? `Ledger entry ${c.entry} has` : 'A ledger entry has'} been written for this cost. It can no longer be moved or edited; a correction is made with a credit note, recorded under this same transaction.`),
  paid: S('Paid', (c) => `Paid${c.date ? ` ${c.date}` : ''}. It can no longer be moved or edited; a correction is made with a credit note.`),
  credited: S('Credited', () => 'A credit note covers this cost. It is recorded under this same transaction.'),
  dispatch_failed: S('Failed to reach the Ledger', () => "Core approved this cost but has not been able to get it to the Ledger. Core's dispatch is not confirmed."),
  refused: S('Rejected', (c) => `This cost was rejected${c.who ? ` by ${c.who}` : ''}${c.reason ? `: "${c.reason}"` : ''}. It is not being billed, and it can be restored.`),
  voided: S('Cancelled', () => 'The booking was cancelled before this cost was billed. It is not being billed, and it stays on record.'),
  billed_elsewhere: S('Billed elsewhere', (c) => `This cost was marked as billed outside this system${c.reason ? `: "${c.reason}"` : ''}.`),
});

// `advice` has four Ledger-owned details that change the label and sentence.
export const STAGE_DETAIL_COPY = Object.freeze({
  coming: S('Coming', () => 'The Ledger knows about this cost, but the session has not settled. You can still move it to the next invoice.'),
  being_checked: S('Being checked', () => 'The Ledger did not put this on an invoice by itself. You can move it to the next invoice.'),
  deferred: S('In Advice', () => 'This cost is in Advice, not on an invoice. It can be moved to the next invoice.'),
  rejected: S('Rejected', () => 'This cost is marked as not to be billed. It can be restored, or moved to the next invoice.'),
});

// Failure stages use the existing problem pill; every other stage is neutral (the owner colour says WHO is speaking only).
export const FAILURE_STAGES = Object.freeze(['dispatch_failed', 'refused']);

export const UNKNOWN_STAGE = Object.freeze({
  label: 'Status not recognised',
  sentence: (txId) => `This version of the app does not know this stage yet. The Core and Ledger records are unchanged. Reload, or ask support, quoting ${txId}.`,
});

export function stageLabel(stage, detail) {
  if (stage === 'advice' && STAGE_DETAIL_COPY[detail]) return STAGE_DETAIL_COPY[detail].label;
  return STAGE_COPY[stage] ? STAGE_COPY[stage].label : UNKNOWN_STAGE.label;
}

// The sentence under the pill: Core's `headline` VERBATIM when it sent one, else the fallback (flagged).
export function stageSentence({ stage, detail, headline, txId, ctx = {} }) {
  if (typeof headline === 'string' && headline !== '') return { text: headline, fallback: false };
  if (stage === 'advice' && STAGE_DETAIL_COPY[detail]) return { text: STAGE_DETAIL_COPY[detail].fallback(ctx), fallback: true };
  if (STAGE_COPY[stage]) return { text: STAGE_COPY[stage].fallback(ctx), fallback: true };
  return { text: UNKNOWN_STAGE.sentence(txId || 'this transaction'), fallback: false };
}

// ---- Condition codes (2.4b): the calendar/short badge. The sentence itself is always Core's. ----
export const CONDITION_BADGES = Object.freeze({
  feed_unbound: 'Waiting: Ledger not set up',
  step_up_required: 'Verification needed',
  reason_required: 'Reason needed',
  session_not_ended: 'Session not ended',
  two_sv_unavailable: "Can't verify here",
  quote_changed: 'Price changed',
  currency_mismatch: 'Currency differs',
  vat_rate_required: 'VAT rate needed',
  ledger_unreachable: 'Ledger unreachable',
  duplicate_booking: 'Possible duplicate',
  draft_not_unique: 'Draft needs attention',
  // ACCT-TX-021: not an error, a condition (the TX is not written yet).
  nothing_to_credit: 'Nothing to credit yet',
});

const humanise = (code) => {
  const spaced = String(code || '').replace(/_/g, ' ').trim();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : '';
};
export const conditionBadge = (code) => CONDITION_BADGES[code] || humanise(code);

// ---- Timeline events (2.6): Tag + fallback sentence, one row per journal `action` of the pinned enum. ----
// `fallback(ev, ctx)` is used ONLY when the entry has no `summary`.
const E = (tag, fallback) => ({ tag, fallback });
const detailBeforeAfter = (ev) => {
  const d = ev?.correlation?.detail;
  return d && (d.before || d.after) ? ` ${d.before ?? ''} -> ${d.after ?? ''}`.replace(/\s+/g, ' ') : '';
};
export const EVENT_COPY = Object.freeze({
  captured: E('Booked', (ev) => (ev?.correlation?.gcal_event_id ? 'Booked in Google Calendar.' : 'Booked in the app.')),
  quoted: E('Priced', (ev, ctx) => `The price is ${ctx.after || 'not known'}.`),
  rescheduled: E('Rescheduled', (ev) => `The booking window changed.${detailBeforeAfter(ev)}`),
  settled: E('Session ended', () => 'The session ended.'),
  approval_requested: E('Sent for approval', () => 'Sent for approval.'),
  returned: E('Returned', () => 'Returned for repricing.'),
  released: E('Approved for sending', (ev, ctx) => `Approved${ctx.who ? ` by ${ctx.who}` : ''}.`),
  retry_requested: E('Retry requested', () => 'A retry was requested.'),
  rearmed: E('Re-armed', () => 'Sending was re-armed.'),
  dispatch_attempted: E('Sending to the Ledger', () => 'Core tried to send this to the Ledger.'),
  dispatch_failed: E('Failed to reach the Ledger', () => 'Core could not get this to the Ledger.'),
  acknowledged: E('Ledger confirmed', () => 'The Ledger confirmed it holds this.'),
  reconciled: E('Matched with the Ledger', () => "Core's post matched the Ledger's copy."),
  moved_to_draft: E('Moved to invoice', () => 'Moved to the next invoice.'),
  moved_to_advice: E('Moved to Advice', () => 'Moved back to Advice.'),
  amount_edited: E('Amount changed', () => 'The amount changed.'),
  description_edited: E('Description changed', (ev) => `The description changed.${detailBeforeAfter(ev)}`),
  attribution_edited: E('Customer changed', () => 'The customer changed.'),
  rejected: E('Rejected', () => 'Rejected.'),
  restored: E('Restored', () => 'Restored.'),
  issued: E('Invoice issued', () => 'The invoice was issued.'),
  posted: E('Posted to the ledger', () => 'The ledger entry was written.'),
  paid: E('Paid', () => 'Payment received.'),
  credit_noted: E('Credit note', () => 'A credit note was raised.'),
  voided: E('Cancelled', () => 'The booking was cancelled.'),
  billed_elsewhere: E('Billed elsewhere', () => 'Marked as billed elsewhere.'),
  mirrored: E('Stage updated from the Ledger', () => 'Core updated its copy of the stage from the Ledger.'),
  series_changed: E('Series edited', () => 'A change to the booking series was applied to this session.'),
  change_not_applied: E('Change not applied', () => 'A change was not applied to this transaction.'),
  billed_by_hand: E('Billed by hand', () => 'This cost was billed by hand on the invoice.'),
  corrected: E('Corrected', () => 'An upward correction was billed under this transaction.'),
  // The journal `credited` action (Spec 1.4.0 enum; design 4.7b row "credited" follows `credit_noted`).
  credited: E('Credited', () => 'The credit note covers this cost; the transaction is now credited.'),
  // Spec 1.12.0 journal actions (ML001-F0221/F0222, F0252..F0255): the credit-note lifecycle, the customer's objection, the monthly sweep.
  expired: E('Expired', () => 'The credit note passed its expiry date.'),
  archived: E('Archived', () => 'The credit note was archived.'),
  reinstated: E('Reinstated', () => 'The credit note was brought back to Active.'),
  not_happy: E('Customer not happy', () => 'The customer messaged about the draft; it will not be issued until we have talked.'),
  monthly_sweep: E('Monthly sweep', () => 'The monthly cycle moved this cost onto the draft invoice.'),
  // The credit application policy changed (ML001-F0219/F0220, 1.12.0 final, Spec main 3f14499a): shown in the history drawer like any journal event.
  policy_changed: E('Credit application changed', () => 'The credit application policy was changed.'),
  // Spec 1.13.0 journal actions (envelope claims, ML001-D013): re-pinned with 1.14.0 (WebUI #191), so the T-M004-0205 every-pinned-value rule needs copy.
  claim_submitted: E('Claim submitted', () => 'The claim for this cost was submitted.'),
  claim_approved: E('Claim approved', () => 'The claim for this cost was approved.'),
  claim_posted: E('Claim posted', () => 'The claim for this cost was posted.'),
  claim_reimbursed: E('Claim reimbursed', () => 'The claim for this cost was reimbursed.'),
  // Spec 1.18.0 journal actions (statements, credit allocation, on-account credit): copy for the T-M004-0205 every-pinned-value rule.
  claim_returned: E('Claim returned', () => 'The claim for this cost was returned for changes.'),
  claim_rejected: E('Claim rejected', () => 'The claim for this cost was rejected.'),
  payment_received: E('Payment received', () => 'A payment was received against this cost.'),
  allocated: E('Allocated', () => 'The amount was allocated to this cost.'),
  unallocated: E('Unallocated', () => 'The allocation to this cost was removed.'),
  refunded: E('Refunded', () => 'The amount was refunded.'),
  on_account_credited: E('Credited to account', () => 'The amount was credited to the customer account.'),
  transferred_to_account: E('Transferred to account', () => 'The amount was transferred to the customer account.'),
});

// Actions whose row is styled "needs attention" (a change that did not apply, or a failure).
export const ATTENTION_ACTIONS = Object.freeze(['change_not_applied', 'dispatch_failed']);

export function eventTag(ev) {
  if (ev?.kind === 'anomaly') return 'Needs attention';
  if (ev?.kind === 'note') return 'Note';
  const row = EVENT_COPY[ev?.action];
  return row ? row.tag : humanise(ev?.action);
}

// Core's/the Ledger's summary VERBATIM; the fallback only when none was sent.
export function eventSentence(ev, ctx = {}) {
  if (typeof ev?.summary === 'string' && ev.summary !== '') return { text: ev.summary, fallback: false };
  if (ev?.kind === 'anomaly' || ev?.kind === 'note') {
    if (typeof ev.reason === 'string' && ev.reason !== '') return { text: ev.reason, fallback: false, reasonIsSentence: true };
  }
  const row = EVENT_COPY[ev?.action];
  if (row) return { text: row.fallback(ev, ctx), fallback: true };
  return { text: `${humanise(ev?.action)}.`, fallback: true };
}

// ---- Cause chain (2.5): labels are ours, values are Core's. ----
export const PLAN_CHANGE_FIELD_LABELS = Object.freeze({
  first_hour_rate: 'First-hour rate',
  long_hour_rate: 'Long-hour rate',
  long_rate_threshold_minutes: 'Long-rate threshold (minutes)',
  status: 'Status',
  effective_from: 'Effective from',
  effective_to: 'Effective to',
  name: 'Name',
});

export const amountText = (minor, currency) => (minor == null ? '' : formatMinorAmount(minor, currency));
